import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Package, X, ShoppingCart, Plus, Minus, Trash2,
  ChevronLeft, ChevronRight, Barcode, Printer, CheckCircle, Loader2,
  CreditCard, Smartphone, Wallet, Banknote, Users, Percent, DollarSign,
  User, FileText, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import Cart from '../components/pos/Cart';
import ProductSearch from '../components/pos/ProductSearch';
import CustomerSelector from '../components/pos/CustomerSelector';
import QuickProductAdd from '../components/pos/QuickProductAdd';
import SaleUnitSelector from '../components/pos/SaleUnitSelector';
import PaymentPanel from '../components/pos/PaymentPanel';
import Receipt from '../components/pos/Receipt';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getPiecesForUnit } from '../utils/formatStock';
import { fetchProducts } from '../redux/slices/productSlice';
import { createSale } from '../redux/slices/saleSlice';
import { fetchCustomers, createCustomer } from '../redux/slices/customerSlice';
import { fetchShopInfo } from '../redux/slices/settingSlice';

const categories = ['All', 'Pan Masala', 'Gutka', 'Supari', 'Mouth Freshener', 'Cigarettes', 'Beverages', 'Snacks', 'Other'];

export default function POS() {
  const dispatch = useDispatch();
  const { products, loading: productLoading } = useSelector((state) => state.products);
  const { customers, loading: customerLoading } = useSelector((state) => state.customers);
  const { user } = useSelector((state) => state.auth);
  const { settings, shopInfo } = useSelector((state) => state.settings);

  const [cartItems, setCartItems] = useState([]);
  const [discount, setDiscount] = useState('');
  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [showUnitSelector, setShowUnitSelector] = useState(false);
  const [unitSelectorProduct, setUnitSelectorProduct] = useState(null);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const barcodeInputRef = useRef(null);
  const searchInputRef = useRef(null);

  const taxRate = settings?.taxRate || 0;

  useEffect(() => {
    dispatch(fetchProducts({ page: 1, limit: 200 }));
    dispatch(fetchCustomers({ page: 1, limit: 200 }));
    dispatch(fetchShopInfo());
  }, [dispatch]);

  const filteredProducts = useMemo(() => {
    let result = products;
    if (activeCategory !== 'All') {
      result = result.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          (p.productName || p.name)?.toLowerCase().includes(q) ||
          (p.barcode || p.serialNumber)?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [products, activeCategory, searchQuery]);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.salePrice || item.price || 0) * (item.quantity || 1), 0),
    [cartItems]
  );
  const total = useMemo(() => {
    const discAmt = parseFloat(discount) || 0;
    const taxable = subtotal - discAmt;
    const taxAmt = taxable * (taxRate / 100);
    return taxable + taxAmt;
  }, [subtotal, discount, taxRate]);

  const addToCart = useCallback((product) => {
    if (product.piecesPerPack && product.packsPerBox) {
      setUnitSelectorProduct(product);
      setShowUnitSelector(true);
      return;
    }
    setCartItems((prev) => {
      const existing = prev.find((item) => (item._id || item.id) === (product._id || product.id) && !item.saleUnit);
      if (existing) {
        return prev.map((item) =>
          (item._id || item.id) === (product._id || product.id) && !item.saleUnit
            ? { ...item, quantity: (item.quantity || 1) + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1, saleUnit: 'PIECE', piecesDeducted: 1 }];
    });
    toast.success(`${product.productName || product.name} added to cart`);
  }, []);

  const handleAddWithUnit = useCallback(({ unit, quantity, price }) => {
    const product = unitSelectorProduct;
    if (!product) return;
    const piecesDeducted = getPiecesForUnit(unit, quantity, product.piecesPerPack, product.packsPerBox);
    setCartItems((prev) => [...prev, {
      ...product,
      _id: product._id || product.id,
      quantity,
      salePrice: price,
      saleUnit: unit,
      piecesDeducted,
    }]);
    setShowUnitSelector(false);
    setUnitSelectorProduct(null);
    toast.success(`${quantity} x ${product.productName || product.name} (${unit}) added`);
  }, [unitSelectorProduct]);

  const updateQuantity = useCallback((item, quantity) => {
    setCartItems((prev) =>
      prev.map((i) =>
        (i._id || i.id) === (item._id || item.id) ? { ...i, quantity: Math.max(0, quantity) } : i
      )
    );
  }, []);

  const removeItem = useCallback((item) => {
    setCartItems((prev) => prev.filter((i) => (i._id || i.id) !== (item._id || item.id)));
    toast.success('Item removed from cart');
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setDiscount('');
    setNotes('');
    setCustomer(null);
    toast.success('Cart cleared');
  }, []);

  const handleBarcodeSearch = useCallback(
    (barcode) => {
      const product = products.find((p) => p.barcode === barcode);
      if (product) {
        addToCart(product);
      } else {
        toast.error(`No product found with barcode: ${barcode}`);
      }
    },
    [products, addToCart]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'F1') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'F2') {
        e.preventDefault();
        if (cartItems.length > 0) setShowPayment(true);
      } else if (e.key === 'Escape') {
        setShowPayment(false);
        setShowReceipt(false);
        setShowQuickAdd(false);
      }
    },
    [cartItems.length]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleCheckout = async (paymentData) => {
    setPaymentLoading(true);
    try {
      const isCredit = paymentData.method === 'credit';
      const result = await dispatch(
        createSale({
          items: cartItems.map((item) => ({
            productId: item.productId || item._id || item.id,
            productName: item.productName || item.name,
            quantity: item.quantity,
            unitPrice: item.salePrice || item.price || 0,
            itemTotal: (item.salePrice || item.price || 0) * item.quantity,
            saleUnit: item.saleUnit || 'PIECE',
            piecesDeducted: item.piecesDeducted || item.quantity,
          })),
          subtotal,
          discount: parseFloat(discount) || 0,
          tax: (subtotal - (parseFloat(discount) || 0)) * (taxRate / 100),
          total,
          paymentMethod: isCredit ? 'CREDIT' : paymentData.method === 'online' ? 'ONLINE' : 'CASH',
          paymentStatus: isCredit ? 'PENDING' : 'PAID',
          amountPaid: isCredit ? 0 : (paymentData.amountTendered || total),
          amountPending: isCredit ? total : 0,
          paymentDetails: paymentData,
          customerId: customer?.customerId || customer?.id || customer?._id || null,
          customerName: customer?.fullName || customer?.name || null,
          notes,
          operator: user?.name || user?.fullName || 'Operator',
        })
      ).unwrap();

      setLastTransaction({
        ...result.sale || result,
        items: cartItems,
        subtotal,
        discount: parseFloat(discount) || 0,
        tax: (subtotal - (parseFloat(discount) || 0)) * (taxRate / 100),
        total,
        paymentMethod: paymentData.method,
      });
      setShowPayment(false);
      setShowReceipt(true);
      setCartItems([]);
      setDiscount('');
      setNotes('');
      setCustomer(null);
      toast.success('Sale completed successfully!');
    } catch (err) {
      toast.error(err?.message || 'Checkout failed');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleCustomerCreate = async (data) => {
    try {
      const result = await dispatch(createCustomer(data)).unwrap();
      setCustomer(result.customer || result);
      toast.success('Customer created');
    } catch (err) {
      toast.error(err?.message || 'Failed to create customer');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-[calc(100vh-64px)] flex flex-col"
    >
      <div className="hidden">
        <input
          ref={barcodeInputRef}
          type="text"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleBarcodeSearch(e.target.value);
              e.target.value = '';
            }
          }}
          autoComplete="off"
        />
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 overflow-hidden">
        <div className="flex-1 flex flex-col gap-4 min-h-0">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                <Search size={20} />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products by name, barcode, or SKU... (F1)"
                className="w-full pl-11 pr-4 py-3.5 text-base bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-primary-500 dark:focus:border-primary-500 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-colors"
                autoFocus
              />
            </div>
            <Button
              variant="secondary"
              size="md"
              icon={Plus}
              onClick={() => setShowQuickAdd(true)}
            >
              Quick Add
            </Button>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
            {categories.map((cat) => (
              <motion.button
                key={cat}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 text-sm font-medium rounded-xl whitespace-nowrap transition-all ${
                  activeCategory === cat
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {cat}
              </motion.button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {productLoading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner size="lg" message="Loading products..." />
              </div>
            ) : filteredProducts.length === 0 ? (
              <EmptyState
                icon={Package}
                title="No products found"
                description={searchQuery ? 'Try a different search term' : 'No products in this category'}
                actionLabel="Add Quick Product"
                onAction={() => setShowQuickAdd(true)}
              />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 pb-4">
                {filteredProducts.map((product, idx) => {
                  const outOfStock = (product.stock || product.quantity || 0) <= 0;
                  return (
                    <motion.button
                      key={product._id || product.id || idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.02 }}
                      whileHover={outOfStock ? {} : { y: -3, scale: 1.02 }}
                      whileTap={outOfStock ? {} : { scale: 0.98 }}
                      onClick={() => !outOfStock && addToCart(product)}
                      disabled={outOfStock}
                      className={`relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-left transition-all ${
                        outOfStock ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3 mx-auto">
                        {product.image ? (
                          <img src={product.image} alt={product.productName || product.name} className="w-full h-full object-cover rounded-xl" />
                        ) : (
                          <Package size={24} className="text-gray-400" />
                        )}
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white text-center truncate mb-1">
                        {product.productName || product.name}
                      </h4>
                      <p className="text-lg font-bold text-primary-600 dark:text-primary-400 text-center">
                        Rs. {(product.salePrice || product.price || 0).toLocaleString()}
                      </p>
                      <p className={`text-xs text-center mt-1 ${
                        outOfStock ? 'text-red-500' : (product.stock || product.quantity || 0) <= 5 ? 'text-amber-500' : 'text-gray-400 dark:text-gray-500'
                      }`}>
                        {outOfStock ? 'Out of Stock' : `${product.stock || product.quantity || 0} pcs`}
                      </p>
                      {outOfStock && (
                        <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 rounded-xl flex items-center justify-center">
                          <span className="px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-full">Out of Stock</span>
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="w-full lg:w-[420px] xl:w-[480px] flex flex-col">
          <Cart
            items={cartItems}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeItem}
            onClearCart={clearCart}
            discount={discount}
            onDiscountChange={setDiscount}
            taxRate={taxRate}
            customer={customer}
            onCustomerChange={() => setShowCustomerSelect(true)}
            notes={notes}
            onNotesChange={setNotes}
            onCheckout={() => {
              if (cartItems.length > 0) setShowPayment(true);
            }}
          />
        </div>
      </div>

      <Modal isOpen={showPayment} onClose={() => setShowPayment(false)} size="md" title="Payment">
        <PaymentPanel
          totalAmount={total}
          onConfirm={handleCheckout}
          onCancel={() => setShowPayment(false)}
          loading={paymentLoading}
          customer={customer}
        />
      </Modal>

      <Modal isOpen={showReceipt} onClose={() => setShowReceipt(false)} size="sm" showCloseButton={false}>
        <Receipt
          transaction={lastTransaction}
          shopName={shopInfo?.name || 'Devoard POS'}
          shopAddress={shopInfo?.address || ''}
          shopPhone={shopInfo?.phone || ''}
          onPrint={() => {
            toast.success('Printing receipt...');
          }}
          onEmail={() => {
            toast.success('Email sent');
          }}
          onDownload={() => {
            toast.success('Receipt downloaded');
          }}
          onClose={() => setShowReceipt(false)}
        />
      </Modal>

      <Modal isOpen={showQuickAdd} onClose={() => setShowQuickAdd(false)} size="sm" title="Quick Product Add">
        <QuickProductAdd
          onAdd={(product) => {
            addToCart(product);
            setShowQuickAdd(false);
          }}
          onCancel={() => setShowQuickAdd(false)}
        />
      </Modal>

      <Modal isOpen={showCustomerSelect} onClose={() => setShowCustomerSelect(false)} size="sm" title="Select Customer">
        <CustomerSelector
          customers={customers}
          selectedCustomer={customer}
          onSelect={(c) => {
            setCustomer(c);
            setShowCustomerSelect(false);
          }}
          onClear={() => {
            setCustomer(null);
          }}
          onCreateNew={(data) => {
            handleCustomerCreate({ fullName: data?.search || '', phoneNumber: '' });
            setShowCustomerSelect(false);
          }}
          loading={customerLoading}
        />
      </Modal>

      <Modal isOpen={showUnitSelector} onClose={() => { setShowUnitSelector(false); setUnitSelectorProduct(null); }} size="sm" title="Select Unit">
        {unitSelectorProduct && (
          <SaleUnitSelector
            product={unitSelectorProduct}
            onConfirm={handleAddWithUnit}
            onCancel={() => { setShowUnitSelector(false); setUnitSelectorProduct(null); }}
          />
        )}
      </Modal>
    </motion.div>
  );
}
